// ink-component
import React, { ReactNode, HTMLProps } from 'react';
import flattenChildren from 'react-keyed-flatten-children';

import { SpacingType, VStackAlignType } from '../../helpers/constants/layoutPropTypes';

import Frame from '../Frame/Frame';
import StyledBox from '../StyledBox/StyledBox';
import Divider from '../Divider/Divider';
import { LayoutHTMLType } from '../_common/Types/CustomTypes';

export interface VStackProps extends HTMLProps<LayoutHTMLType> {
  /** Contents. */
  children?: ReactNode;
  /** Alignment property. May be set as an array for responsive behavior. */
  align?: VStackAlignType | VStackAlignType[];
  /** Set gap spacing between children. Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  spacing?: SpacingType;
  /** Defines the type of divider. Don't set for no dividers. */
  dividers?: 'regular' | 'strong';
  /** Tag that will be rendered by the Component. */
  as?: 'div' | 'ol' | 'ul' | 'span';
  /** Tag that will be rendered by the Component's immediate children. */
  childrenAs?: string;
}

/**
 * @purpose Layout items in a vertical stack
 * @group Layout
 * @position 1
 * @categories layout
 * @aka column, flexbox
 * @HTMLType LayoutHTMLType
 */
function VStack(props: VStackProps) {
  const {
    className,
    style,
    children,
    align = 'stretch',
    spacing = 'gutter',
    dividers,
    as,
    childrenAs,
    ...otherProps
  } = props;

  const resolveAlignment = align => {
    const aligmentMap = {
      left: 'start',
      center: 'center',
      right: 'end',
      stretch: 'stretch',
    };

    if (typeof align === 'string') {
      return aligmentMap[align];
    }
    if (typeof align === 'object') {
      return align.map(item => aligmentMap[item]);
    }
    return 'start';
  };

  const isList = as === 'ul' || as === 'ol';

  return (
    <Frame>
      <StyledBox
        otherProps={otherProps}
        component={as}
        display="grid"
        justifyItems={resolveAlignment(align)}
        gridGap={spacing}
        gridTemplateColumns="minmax(auto, 100%)"
      >
        {React.Children.map(flattenChildren(children), (child, index) => (
          <>
            {dividers && index ? (
              <StyledBox component={isList ? 'li' : childrenAs} position="relative" height="0" width="100%">
                <Divider weight={dividers} />
              </StyledBox>
            ) : null}
            <StyledBox component={isList ? 'li' : childrenAs} display="grid" gridTemplateColumns="inherit">
              {child}
            </StyledBox>
          </>
        ))}
      </StyledBox>
    </Frame>
  );
}

VStack.defaultProps = {
  children: undefined,
  align: 'stretch',
  spacing: 'gutter',
  dividers: undefined,
  as: 'div',
  childrenAs: 'div',
};

VStack.displayName = 'VStack';

export default VStack;
